import type React from 'react'

import {
  type BrokersTaskStatus,
  type BrokersWorkflowAvailableActionsItem,
  type BrokersWorkflowStatus,
} from 'utils/web_api.schemas'

// Re-export badge components for backwards compatibility
export {
  ClickableBadgeWrapper,
  TaskStatusBadge,
  WorkflowStatusBadge,
} from './badges'
export {
  CoverageBadge,
  EnrollmentEventBadge,
  ExecutionContextBadge,
  ExecutionMethodBadge,
  WorkflowTypeBadge,
} from './badges'

// Workflow visual styling functions
export const getWorkflowBorderColor = (
  status: BrokersWorkflowStatus
): string => {
  switch (status) {
    case 'pending':
      return 'border-l-4 border-slate-400 bg-slate-50' // Neutral/slate border - ready to start, no urgency
    case 'in_progress':
      return 'border-l-4 border-blue-400 bg-blue-50' // Info/blue border with subtle background
    case 'completed':
      return 'border-l-4 border-green-400 bg-green-50' // Success/green border with subtle background
    case 'canceled':
      return 'border-l-4 border-red-400 bg-red-50' // Critical/red border with subtle background
    case 'paused':
      return 'border-l-4 border-amber-400 bg-amber-50' // Warning/amber border - needs attention
    default:
      return 'border-l-4 border-gray-400 bg-gray-50' // Default/gray border with subtle background
  }
}

// Enhanced task styling - only left border for blocked tasks, backgrounds handled by CSS-in-JS
export const getTaskContainerStyle = (task: {
  can_execute: boolean
  status: BrokersTaskStatus
}): string => {
  const isNotExecutableAndIncomplete =
    !task.can_execute && (task.status === 'pending' || task.status === 'excluded')

  // Base classes for all tasks
  let baseClasses = 'transition-all duration-200'

  // Add blocked task styling with left border emphasis only
  if (isNotExecutableAndIncomplete) {
    baseClasses += ' opacity-60 border-l-4 border-amber-400'
  }

  return baseClasses
}

// Get status-based background color for header only
export const getTaskHeaderBackgroundStyle = (task: {
  can_execute: boolean
  status: BrokersTaskStatus
}): React.CSSProperties => {
  let backgroundColor = ''

  switch (task.status) {
    case 'completed':
      backgroundColor = 'rgb(240 253 244)' // bg-green-50
      break
    case 'failed':
      backgroundColor = 'rgb(254 242 242)' // bg-red-50
      break
    case 'canceled':
      backgroundColor = 'rgb(243 244 246)' // bg-gray-100
      break
    case 'excluded':
      backgroundColor = 'rgb(249 250 251)' // bg-gray-50
      break
    case 'pending':
      if (task.can_execute) {
        backgroundColor = 'rgb(239 246 255)' // bg-blue-50 (ready to execute)
      } else {
        backgroundColor = 'rgb(255 251 235)' // bg-amber-50 (blocked/waiting)
      }
      break
    default: // in_progress and others
      backgroundColor = 'rgb(240 249 255)' // bg-sky-50
  }

  return { backgroundColor }
}

// Get status-based background color for workflow header
export const getWorkflowHeaderBackgroundStyle = (
  status: BrokersWorkflowStatus
): React.CSSProperties => {
  let backgroundColor = ''

  switch (status) {
    case 'pending':
      backgroundColor = 'rgb(248 250 252)' // bg-slate-50
      break
    case 'in_progress':
      backgroundColor = 'rgb(239 246 255)' // bg-blue-50
      break
    case 'completed':
      backgroundColor = 'rgb(240 253 244)' // bg-green-50
      break
    case 'canceled':
      backgroundColor = 'rgb(254 242 242)' // bg-red-50
      break
    case 'paused':
      backgroundColor = 'rgb(255 251 235)' // bg-amber-50
      break
    default:
      backgroundColor = 'rgb(249 250 251)' // bg-gray-50
  }

  return { backgroundColor }
}

// Get task container hover effects
export const getTaskHoverStyle = (task: {
  can_execute: boolean
  status: BrokersTaskStatus
}): string => {
  const isExecutable = task.can_execute && task.status === 'pending'

  if (isExecutable) {
    return 'hover:shadow-md hover:border-blue-300 cursor-pointer'
  }

  return 'hover:shadow-sm'
}

// Task styling helper for compact views (like WorkflowSummary)
export const getTaskCompactStyle = (task: {
  can_execute: boolean
  status: BrokersTaskStatus
}): string => {
  const isNotExecutableAndIncomplete =
    !task.can_execute && (task.status === 'pending' || task.status === 'excluded')

  return isNotExecutableAndIncomplete
    ? 'opacity-60 border-l-4 border-yellow-300'
    : ''
}

// Workflow action sorting utility
export const sortAvailableActions = (
  actions: BrokersWorkflowAvailableActionsItem[] | undefined
) => {
  if (!actions) return []

  return actions.sort((a, b) => {
    const order = ['start', 'resume', 'pause', 'complete', 'cancel', 'reopen']
    return order.indexOf(a) - order.indexOf(b)
  })
}
